import React, { useCallback, useContext, useEffect } from 'react';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import { CommentPublisherResult } from '../../../CommentEditor/redux/types';
import { focusEditor } from '../../../CommentEditor/redux/actionCreators';
import CommentBox from '../../../CommentBox/presets/CommentBox';
import { CommentContext } from '../../CommentContext';

export interface Props {
  canPostComment?: boolean;
  className?: string;

  /** Called once a comment edit has been submitted. */
  onCommentUpdated?: (result: CommentPublisherResult) => void;
}

/** A comment edit view we use for most comment presets. As of writing, this is just our CommentBox with some filled in fields. */
const EditView: React.FC<Props> = ({
  className,
  canPostComment,
  onCommentUpdated,
}) => {
  const dispatchFocusEditor = useAction(focusEditor);
  const { comment, setEditingState } = useContext(CommentContext);

  const onCloseEditor = useCallback(() => {
    setEditingState('none');
  }, [setEditingState]);

  const onEditDone = useCallback(
    (result: CommentPublisherResult) => {
      setEditingState('none');
      onCommentUpdated && result.comment && onCommentUpdated(result);
    },
    [setEditingState, onCommentUpdated]
  );

  const editorId = `edit-${comment.commentId}`;

  useEffect(() => {
    dispatchFocusEditor(editorId);
  }, [dispatchFocusEditor, editorId]);

  return (
    <CommentBox
      className={className}
      onPublishSuccess={onEditDone}
      id={editorId}
      commentableType={comment.typeId}
      commentableItemId={comment.itemId}
      editComment={comment}
      onClose={onCloseEditor}
      canPostComment={canPostComment}
    />
  );
};
EditView.displayName = 'EditView';

export default EditView;
